import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Bell, 
  Search, 
  Menu,
  X,
  Sparkles,
  Crown,
  Settings,
  LogOut,
  User,
  Wallet,
  Shield,
  Heart,
  Calendar,
  ShieldCheck,
  Map
} from 'lucide-react'
import { cn, getImageUrl } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { getSafeAvatarUrl } from '@/utils/avatar'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'

export function ModernTopBar() {
  const { user, logout, signOut, session, profile } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [notifications] = useState(3) // Mock notification count
  
  // Helper to get profile URL with better fallback
  const getProfileUrl = () => {
    if (user?.username) {
      return `/profile/@${user.username}`
    }
    if (user?.id) {
      return `/profile/${user.id}`
    }
    if (session?.user?.id) {
      return `/profile/${session.user.id}`
    }
    return '/profile'
  }
  
  const profileUrl = getProfileUrl()
  
  // Debug profile URL construction
  console.log('🎆 ModernTopBar profile URL debug:', {
    'user?.username': user?.username,
    'user?.id': user?.id,
    'user?.email': user?.email,
    'session?.user?.id': session?.user?.id,
    'session?.user?.email': session?.user?.email,
    'session?.user?.user_metadata': session?.user?.user_metadata,
    'finalProfileUrl': profileUrl,
    'userObject': user
  })

  const menuItems = [
    { icon: User, label: 'My Profile', path: profileUrl },
    { icon: Wallet, label: 'Wallet', path: '/wallet' },
    { icon: Heart, label: 'Matches', path: '/dating' },
    { icon: Shield, label: 'Safety', path: '/profile/settings' },
    { icon: Settings, label: 'Settings', path: '/profile/settings' },
    { icon: Map, label: 'Page Navigator', path: '/page-navigator' },
  ]

  const adminItems = [
    ...(profile?.effectiveRole?.role_name === 'admin' || profile?.effectiveRole?.role_name === 'super_admin' ? [{ icon: ShieldCheck, label: 'Admin Dashboard', path: '/admin' }] : []),
    ...(profile?.organizerProfile ? [{ icon: Calendar, label: 'Event Manager', path: '/events/manage' }] : []),
  ]

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] safe-area-inset-top isolate">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-dark-950/80 backdrop-blur-xl border-b border-kinky-600/20" />
      
      <div className="relative flex items-center justify-between h-16 px-4 z-10">
        {/* Left Section - Logo */}
        <div className="flex items-center gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-kinky-600 to-passion-600 rounded-xl flex items-center justify-center shadow-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -inset-1 bg-gradient-to-br from-kinky-600 to-passion-600 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity" />
            </motion.div>
            <span className="hidden sm:block text-xl font-bold bg-gradient-to-r from-kinky-400 to-passion-400 bg-clip-text text-transparent">
              KinkyApp
            </span>
          </Link>
        </div>

        {/* Center Section - Search (Desktop Only) */}
        <div className="hidden sm:flex flex-1 max-w-xl mx-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Search people, events, content..."
              className="w-full pl-10 pr-4 py-2.5 bg-dark-900/50 border border-dark-700 rounded-full text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-kinky-500 focus:border-kinky-500 transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-dark-700 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-dark-400" />
              </button>
            )}
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative p-2 hover:bg-dark-800/50 rounded-full transition-colors"
          >
            <Bell className="w-6 h-6 text-white" />
            {notifications > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[20px] h-5 px-1 bg-passion-500 text-white text-xs font-bold rounded-full"
              >
                {notifications}
              </motion.span>
            )}
          </motion.button>

          {/* User Profile Link */}
          <Link to={profileUrl}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <Avatar
                className={cn(
                  "w-10 h-10 ring-2 ring-offset-2 ring-offset-dark-950",
                  user?.isPremium ? "ring-luxury-500" : "ring-dark-700"
                )}
              >
                <img 
                  src={getSafeAvatarUrl(user?.avatar)} 
                  alt={user?.displayName || 'User'}
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => {
                    // Use the default placeholder on error
                    e.currentTarget.src = getSafeAvatarUrl(null)
                  }}
                />
              </Avatar>
              {user?.isPremium && (
                <div className="absolute -bottom-1 -right-1 p-1 bg-gradient-to-br from-luxury-600 to-luxury-500 rounded-full">
                  <Crown className="w-3 h-3 text-dark-900" />
                </div>
              )}
            </motion.button>
          </Link>

          {/* Burger Menu */}
          <Sheet modal={true}>
            <SheetTrigger asChild>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 hover:bg-dark-800/50 rounded-full transition-colors"
              >
                <motion.div
                  animate={{ rotate: 0 }}
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.3 }}
                >
                  <Menu className="w-6 h-6 text-white" />
                </motion.div>
              </motion.button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-dark-950 border-l border-dark-800">
              <SheetHeader>
                <SheetTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-kinky-500" />
                  Menu
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-800 transition-all duration-200 group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <Icon className="w-5 h-5 text-dark-400 group-hover:text-kinky-400 transition-colors" />
                      </motion.div>
                      <span className="text-white group-hover:text-kinky-400 transition-colors">
                        {item.label}
                      </span>
                    </Link>
                  )
                })}
                
                {/* Admin/Event Manager Links */}
                {adminItems.length > 0 && (
                  <>
                    <div className="my-4 border-t border-dark-800" />
                    {adminItems.map((item) => {
                      const Icon = item.icon
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-dark-800 transition-all duration-200 group"
                        >
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ type: "spring", stiffness: 400 }}
                          >
                            <Icon className="w-5 h-5 text-luxury-400 group-hover:text-luxury-300 transition-colors" />
                          </motion.div>
                          <span className="text-white group-hover:text-luxury-300 transition-colors">
                            {item.label}
                          </span>
                        </Link>
                      )
                    })}
                  </>
                )}
                
                <div className="my-4 border-t border-dark-800" />
                
                {/* Logout Button */}
                <button
                  onClick={() => signOut ? signOut() : logout()}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 transition-all duration-200 group text-left"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <LogOut className="w-5 h-5 text-dark-400 group-hover:text-red-400 transition-colors" />
                  </motion.div>
                  <span className="text-white group-hover:text-red-400 transition-colors">
                    Logout
                  </span>
                </button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>

    </header>
  )
}